import Phaser from 'phaser';
import { useGameStore } from '../store/gameStore';
import type { CueBallPosition, Game, RackLetter } from '@game/models/Game';
import { getCurrentPlayer, updatePhase, switchPlayer, isGameOver, incrementConsecutivePasses, isStalemate, updateStatus } from '@game/models/Game';
import type { Board } from '@game/models/Board';
import { addPlacedWord, getCell, updateCell } from '@game/models/Board';
import type { Letter } from '@game/models/Letter';
import { placeLetterOnCell } from '@game/models/BoardCell';
import type { BonusType } from '@game/types';
import { BOARD_SIZE, CENTER_POSITION } from '@game/constants/gameConstants';
import { type PlacedLetterPosition, getFormedWord, getCrosswords, getCompleteWordForScoring } from '@game/rules/PlacementValidator';
import { submitWord } from '@game/services/WordSubmissionService';
import { calculateWordScore } from '@game/services/ScoreCalculator';
import { getPlayerColor } from '@game/utils/PlayerColors';
import { MultiplayerManager } from '../multiplayer/managers/MultiplayerManager';
import type { WordBoardSnapshot } from '../multiplayer/types/NetworkTypes';
import { COLORS, TYPOGRAPHY } from '../ui/theme';

/**
 * BoardScene: Word-building gameplay scene
 * Players place their collected letters on the board to form words
 */
interface PlacedLetterData {
  letterId: string;
  position: { row: number; col: number };
  sprite: Phaser.GameObjects.Container;
}

export class BoardScene extends Phaser.Scene {
  private board!: Board;
  private cellSize = 35; // pixels per cell
  private gridOffsetX = 250; // Leave room for UI on left (wider for vertical buttons)
  private gridOffsetY = 65; // Leave room for header
  private boardGraphics!: Phaser.GameObjects.Graphics;
  private boardContainer!: Phaser.GameObjects.Container;
  
  // Bonus colors (Scrabble-inspired)
  private readonly BONUS_COLORS: Record<BonusType, number> = {
    'DL': 0x87CEEB, // Light blue (Double Letter)
    'TL': 0x4169E1, // Dark blue (Triple Letter)
    'DW': 0xFFB6C1, // Pink (Double Word)
    'TW': 0xFF6347, // Red/Tomato (Triple Word)
  };
  
  // Letter tile management
  private handTiles: Map<string, Phaser.GameObjects.Container> = new Map();
  private placedLetters: Map<string, PlacedLetterData> = new Map();
  private permanentLetterContainers: Phaser.GameObjects.Container[] = []; // Track rendered board letters
  private handContainer!: Phaser.GameObjects.Container;
  private handLabel?: Phaser.GameObjects.Text;
  private tileSize = 50; // Size of letter tiles
  private handX = 1050; // X position of hand area (right side, aligned with scores)
  
  // Score display tracking
  private scoreTexts: Map<number, Phaser.GameObjects.Text> = new Map();
  
  // Timer
  private timerText?: Phaser.GameObjects.Text;

  private timerDurationMs = 0;
  private timerStartTime = 0;
  private timerExpired = false;
  
  // Word preview
  private wordPreviewText?: Phaser.GameObjects.Text;
  private appliedSnapshotVersion = -1;
  private syncGuardText?: Phaser.GameObjects.Text;

  private endMultiplayerTurnIfNeeded(game: ReturnType<typeof useGameStore.getState>['game'], options?: { gameCompleted?: boolean }): void {
    if (!game) {
      return;
    }

    const multiplayerManager = MultiplayerManager.getInstance();
    if (!this.isMultiplayerSessionActive()) {
      return;
    }

    const sessionRack = multiplayerManager.getSessionState()?.gameState?.currentRackLetters as RackLetter[] | undefined;
    const rackForHandoff = this.resolveRackForTurnHandoff(game.currentRackLetters, sessionRack);
    const sessionCueBall = multiplayerManager.getSessionState()?.gameState?.cueBallPosition as CueBallPosition | null | undefined;
    const cueBallForHandoff = this.resolveCueBallForTurnHandoff(game.cueBallPosition, sessionCueBall);

    if (rackForHandoff !== game.currentRackLetters) {
      console.warn('[BoardScene] Using authoritative session rack for end-turn handoff to avoid stale local overwrite', {
        localRackSize: game.currentRackLetters.length,
        sessionRackSize: Array.isArray(sessionRack) ? sessionRack.length : null,
      });
    }

    const wordBoardSnapshot = this.buildWordBoardSnapshotForHandoff(game);

    multiplayerManager.endTurn({
      currentRackLetters: rackForHandoff,
      cueBallPosition: cueBallForHandoff,
      playerHands: game.players.reduce((acc, player) => {
        acc[player.id] = [...player.hand];
        return acc;
      }, {} as Record<number, Letter[]>),
      playerScores: game.players.reduce((acc, player) => {
        acc[player.id] = player.score;
        return acc;
      }, {} as Record<number, number>),
      consecutivePassesPerPlayer: game.consecutivePassesPerPlayer,
      ...(wordBoardSnapshot ? { wordBoardSnapshot } : {}),
      ...(options?.gameCompleted ? { gameCompleted: true } : {}),
    });
  }

  private resolveRackForTurnHandoff(localRack: RackLetter[], sessionRack?: RackLetter[]): RackLetter[] {
    if (!Array.isArray(sessionRack)) {
      return localRack;
    }

    if (sessionRack.length > 0 && localRack.length === 0) {
      return sessionRack;
    }

    return localRack;
  }

  private resolveCueBallForTurnHandoff(
    localCueBall: CueBallPosition | null,
    sessionCueBall?: CueBallPosition | null
  ): CueBallPosition | null {
    if (localCueBall) {
      return localCueBall;
    }

    if (sessionCueBall !== undefined) {
      return sessionCueBall;
    }

    return null;
  }


  private isMultiplayerSessionActive(): boolean {
    const multiplayerManager = MultiplayerManager.getInstance();
    const sessionState = multiplayerManager.getSessionState();
    return multiplayerManager.isInSession() && !!sessionState?.isMultiplayer;
  }

  private buildWordBoardSnapshotForHandoff(game: Game): WordBoardSnapshot | undefined {
    const multiplayerManager = MultiplayerManager.getInstance();
    const baseRevision = multiplayerManager.getWordBoardSnapshotVersion?.() ?? 0;
    const currentPlayer = getCurrentPlayer(game);

    if (!game.board) {
      return undefined;
    }

    const placedTiles: WordBoardSnapshot['placedTiles'] = [];
    for (let row = 0; row < game.board.cells.length; row += 1) {
      for (let col = 0; col < game.board.cells[row].length; col += 1) {
        const cell = game.board.cells[row][col];
        if (!cell?.letter) {
          continue;
        }

        placedTiles.push({
          row,
          col,
          letterId: cell.letter.id,
          character: cell.letter.character,
          points: cell.letter.points,
          playerId: cell.playerId ?? cell.letter.owner ?? currentPlayer.id,
        });
      }
    }

    const placedWords: WordBoardSnapshot['placedWords'] = (game.board.placedWords ?? []).map((word) => ({
      word: word.word,
      score: word.score,
      playerId: word.playerId,
      turnNumber: word.turnNumber,
      tiles: word.positions.map((position) => ({ row: position.row, col: position.col })),
    }));

    return {
      boardRevision: Math.max(baseRevision + 1, 0),
      updatedAt: new Date().toISOString(),
      updatedByPlayerNumber: currentPlayer.id,
      placedTiles,
      placedWords,
    };
  }

  private buildBoardFromSnapshot(board: Board, snapshot: WordBoardSnapshot): Board {
    let nextBoard: Board = {
      ...board,
      cells: board.cells.map((row) => row.map((cell) => ({
        ...cell,
        letter: null,
        playerId: null,
        wordIds: [],
        isOccupied: false,
      }))),
      placedWords: [],
      isEmpty: snapshot.placedTiles.length === 0,
    };

    const snapshotWords = snapshot.placedWords.map((placedWord, index) => {
      const positions = placedWord.tiles.map((tile) => ({ row: tile.row, col: tile.col }));
      const direction = positions.length > 1 && positions[0].col === positions[1].col ? 'vertical' as const : 'horizontal' as const;
      const letters = positions.map((position, tileIndex) => {
        const tile = snapshot.placedTiles.find((candidate) => candidate.row === position.row && candidate.col === position.col);

        return {
          id: tile?.letterId ?? `${placedWord.word.toLowerCase()}-${tileIndex + 1}`,
          character: (tile?.character ?? placedWord.word[tileIndex] ?? '').toUpperCase() as Letter['character'],
          state: 'on-board' as const,
          owner: tile?.playerId ?? placedWord.playerId,
          points: tile?.points ?? 0,
        };
      });

      return {
        id: `snapshot-word-${snapshot.boardRevision}-${index}`,
        word: placedWord.word,
        positions,
        direction,
        playerId: placedWord.playerId,
        letters,
        score: placedWord.score,
        turnNumber: placedWord.turnNumber,
        isValid: true,
      };
    });

    for (const placedWord of snapshotWords) {
      for (let index = 0; index < placedWord.positions.length; index += 1) {
        const position = placedWord.positions[index];
        const cell = getCell(nextBoard, position);
        const letter = placedWord.letters[index];

        if (!cell || !letter) {
          continue;
        }

        nextBoard = updateCell(nextBoard, position, placeLetterOnCell(cell, letter, placedWord.playerId, placedWord.id));
      }

      nextBoard = addPlacedWord(nextBoard, placedWord);
    }

    return nextBoard;
  }

  private applyAuthoritativeSnapshot(game: Game): Game {
    const multiplayerManager = MultiplayerManager.getInstance();
    const snapshot = multiplayerManager.getWordBoardSnapshot() as WordBoardSnapshot | undefined;
    const snapshotVersion = multiplayerManager.getWordBoardSnapshotVersion();

    if (!multiplayerManager.isInSession() || !snapshot || snapshotVersion < 0) {
      return game;
    }

    if (snapshotVersion <= this.appliedSnapshotVersion) {
      return game;
    }

    this.appliedSnapshotVersion = snapshotVersion;

    return {
      ...game,
      board: this.buildBoardFromSnapshot(game.board, snapshot),
      updatedAt: new Date(),
    };
  }

  private refreshBoardSyncGuardUi(): void {
    const multiplayerManager = MultiplayerManager.getInstance();
    const guard = multiplayerManager.getBoardSyncGuardState?.();

    if (!guard || guard.syncStatus === 'ready') {
      if (this.syncGuardText) {
        this.syncGuardText.destroy();
        this.syncGuardText = undefined;
      }
      return;
    }

    const message = guard.syncStatus === 'waiting'
      ? 'Synchronizing board state...'
      : (guard.blockingReason ?? 'Board sync blocked. Waiting for authoritative state.');

    if (!this.syncGuardText) {
      this.syncGuardText = this.add.text(640, 80, message, {
        fontSize: TYPOGRAPHY.fontSize.base,
        color: guard.syncStatus === 'waiting' ? COLORS.warning.text : COLORS.error.text,
        backgroundColor: guard.syncStatus === 'waiting' ? COLORS.warning.bg : COLORS.error.bg,
        padding: { x: 12, y: 8 },
        fontFamily: TYPOGRAPHY.fontFamily.default,
      }).setOrigin(0.5).setDepth(220).setScrollFactor(0);
      return;
    }

    this.syncGuardText.setText(message);
  }
  
  constructor() {
    super({ key: 'BoardScene' });
  }

  create(): void {
    console.log('BoardScene: create()');
    
    const store = useGameStore.getState();
    const game = store.game;
    
    if (!game) {
      console.error('No game state found');
      return;
    }
    
    // Update game phase and rehydrate from authoritative multiplayer board state when available.
    const updatedGame = this.applyAuthoritativeSnapshot(updatePhase(game, 'board'));
    store.setGame(updatedGame);

    const multiplayerManager = MultiplayerManager.getInstance();
    if (multiplayerManager.isInSession()) {
      multiplayerManager.beginBoardEntrySyncGuard?.();
      this.refreshBoardSyncGuardUi();
      multiplayerManager.onRestoreFailed = (message: string) => {
        this.handleRestoreFailed(message);
      };
    }
    
    // Read timer settings (with defaults for backward compatibility)
    const settings = updatedGame.settings || {
      pocketSize: 'medium' as const,
      showTargetLine: false,
      timerMode: 'off' as const,
    };
    const timerMode = settings.timerMode;
    if (timerMode !== 'off') {
      // Convert timer mode to milliseconds
      const timerMinutes = parseInt(timerMode.replace('min', ''));
      this.timerDurationMs = timerMinutes * 60 * 1000;
      this.timerStartTime = Date.now();
      this.timerExpired = false;
    }
    
    this.board = updatedGame.board;
    const currentPlayer = getCurrentPlayer(updatedGame);
    
    // Setup camera for zooming/panning
    this.cameras.main.setZoom(1);
    this.cameras.main.centerOn(640, 360);
    
    // Render UI first (fixed to camera)
    this.renderUI(currentPlayer.name);
    
    // Create container for board (can be zoomed/panned)
    this.boardContainer = this.add.container(0, 0);
    
    // Render the board grid
    this.renderBoard();
    
    // Render existing placed words from previous turns
    this.renderPlacedWords();
    
    // Create container for hand tiles (fixed to camera)
    this.handContainer = this.add.container(0, 0).setScrollFactor(0).setDepth(50);
    
    // Render player's hand as draggable tiles
    this.renderHandTiles(currentPlayer.hand);
    
    // Add zoom controls
    this.setupCameraControls();
    
    // Add action buttons at bottom
    this.createActionButtons();
  }
  
  update(): void {
    this.refreshBoardSyncGuardUi();

    // Update timer display and enforce wall-clock expiry
    if (this.timerDurationMs > 0) {
      const elapsed = Date.now() - this.timerStartTime;
      const remaining = Math.max(0, this.timerDurationMs - elapsed);

      if (remaining <= 0 && !this.timerExpired) {
        this.timerExpired = true;
        console.log('⏰ Timer expired - auto-passing turn');
        this.showMessage("Time's up!", 'warning');
        // Auto-clear any placed letters so onPass() can proceed without the
        // "Clear letters before passing" guard blocking the turn end.
        this.time.delayedCall(1000, () => {
          Array.from(this.placedLetters.keys()).forEach(id => this.returnTileToHand(id, false));
          this.onPass();
        });
      }

      if (this.timerText) {
        const remainingSeconds = Math.ceil(remaining / 1000);
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        const timeString = `⏱ ${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        this.timerText.setText(timeString);

        if (remainingSeconds <= 10) {
          this.timerText.setColor('#ff0000');
        } else if (remainingSeconds <= 30) {
          this.timerText.setColor('#ffaa00');
        } else {
          this.timerText.setColor('#00ff00');
        }
      }
    }
  }
  
  shutdown(): void {
    const multiplayerManager = MultiplayerManager.getInstance();
    multiplayerManager.onRestoreFailed = undefined;
  }
  
  private createActionButtons(): void {
    // Stack buttons vertically on the left side
    const buttonX = 20;
    let buttonY = 80; // Start below the player indicator
    const buttonSpacing = 54;
    
    // Modern button utility
    const createModernButton = (
      y: number,
      text: string,
      colorMain: number,
      colorHover: number,
      onClick: () => void,
      width: number = 180
    ) => {
      const height = 44;
      
      const shadow = this.add.graphics();
      shadow.fillStyle(0x000000, 0.3);
      shadow.fillRoundedRect(buttonX + 2, y + 2, width, height, 8);
      shadow.setScrollFactor(0).setDepth(59);
      
      const bg = this.add.graphics();
      bg.fillStyle(colorMain, 1);
      bg.fillRoundedRect(buttonX, y, width, height, 8);
      bg.setScrollFactor(0).setDepth(60);
      bg.setInteractive(
        new Phaser.Geom.Rectangle(buttonX, y, width, height),
        Phaser.Geom.Rectangle.Contains
      );
      
      this.add.text(buttonX + width / 2, y + height / 2, text, {
        fontSize: TYPOGRAPHY.fontSize.base,
        color: '#ffffff',
        fontFamily: TYPOGRAPHY.fontFamily.default,
      }).setOrigin(0.5).setScrollFactor(0).setDepth(61);
      
      bg.on('pointerover', () => {
        bg.clear();
        bg.fillStyle(colorHover, 1);
        bg.fillRoundedRect(buttonX, y, width, height, 8);
        this.input.setDefaultCursor('pointer');
      });
      
      bg.on('pointerout', () => {
        bg.clear();
        bg.fillStyle(colorMain, 1);
        bg.fillRoundedRect(buttonX, y, width, height, 8);
        this.input.setDefaultCursor('default');
      });
      
      bg.on('pointerdown', onClick);
    };
    
    // Submit Word button
    createModernButton(buttonY, 'Submit Word', COLORS.success.main, COLORS.success.light, () => this.onSubmitWord());
    buttonY += buttonSpacing;
    
    // Clear Letters button
    createModernButton(buttonY, 'Clear Letters', COLORS.warning.main, COLORS.warning.light, () => this.onClearLetters());
    buttonY += buttonSpacing;
    
    // Pass button
    createModernButton(buttonY, 'Pass', 0x64748b, 0x475569, () => this.onPass());
    buttonY += buttonSpacing + 30; // Extra gap to prevent accidental End Game clicks
    
    // End Game button
    createModernButton(buttonY, 'End Game', COLORS.error.main, COLORS.error.light, () => this.onEndGame());
  }
  
  private onSubmitWord(): void {
    // Check if any letters are placed
    if (this.placedLetters.size === 0) {
      this.showMessage('No letters placed!', 'error');
      return;
    }
    
    const store = useGameStore.getState();
    const game = store.game!;
    const currentPlayer = getCurrentPlayer(game);
    
    // Convert placed letters to submission format
    const placedLetterData: PlacedLetterPosition[] = [];
    
    this.placedLetters.forEach((data, letterId) => {
      // Find the letter in player's hand
      const letter = currentPlayer.hand.find(l => l.id === letterId);
      if (letter) {
        placedLetterData.push({
          letterId: letter.id,
          position: data.position,
          letter: letter.character,
        });
      }
    });
    
    // Submit the word
    const result = submitWord(game, placedLetterData);
    
    if (!result.success) {
      // Show error message
      this.showMessage(result.error || 'Invalid placement', 'error');
      return;
    }
    
    // Success! Update game state
    store.setGame(result.updatedGame!);
    
    this.timerExpired = true; // Prevent timer from firing after turn ends
    
    console.log('After word submission, player scores:', result.updatedGame!.players.map(p => `${p.name}: ${p.score}`).join(', '));
    
    // Update score display
    this.updateScoreDisplay();
    
    // Build detailed score message showing all words
    let message = '';
    
    if (result.mainWord) {
      message = `${result.mainWord.word}: ${result.mainWord.score} pts`;
    }
    
    // Add each crossword on its own line
    if (result.crosswords && result.crosswords.length > 0) {
      result.crosswords.forEach(cw => {
        message += `\n${cw.word}: ${cw.score} pts`;
      });
    }
    
    // Show total if there are crosswords
    if (result.crosswords && result.crosswords.length > 0) {
      message += `\n───────────\nTotal: ${result.score} pts!`;
    } else {
      message += '!';
    }
    
    this.showMessage(message, 'success');
    
    // Clear placed letters from the scene
    this.placedLetters.forEach((data) => {
      data.sprite.destroy();
    });
    this.placedLetters.clear();
    
    // Immediately render the permanent board letters so the word doesn't disappear
    // Update the board reference to the new game state
    this.board = result.updatedGame!.board;
    this.renderPlacedWords();
    
    // Wait a moment, then check if game is over or switch players and continue
    this.time.delayedCall(2000, () => {
      const store = useGameStore.getState();
      const currentGame = store.game!;
      console.log('Inside delayedCall, fetched from store, player scores:', currentGame.players.map(p => `${p.name}: ${p.score}`).join(', '));
      
      // Check if game is over (letter pool exhausted)
      if (isGameOver(currentGame)) {
        // Mark game as completed
        const completedGame = {
          ...currentGame,
          status: 'completed' as const,
        };
        store.setGame(completedGame);
        this.endMultiplayerTurnIfNeeded(completedGame, { gameCompleted: true });
        this.scene.start('GameOverScene');
        return;
      }

      const isMultiplayer = this.isMultiplayerSessionActive();
      this.endMultiplayerTurnIfNeeded(currentGame);

      if (isMultiplayer) {
        // Multiplayer turn ownership is server-authoritative.
        // Do not locally switch players here; wait for turn_change.
        const backToPoolGame = updatePhase(currentGame, 'pool');
        store.setGame(backToPoolGame);
        this.scene.start('PoolScene');
        return;
      }

      // Single-player/local mode: switch to next player immediately.
      const gameWithNextPlayer = switchPlayer(currentGame);
      console.log('🔄 BoardScene: After switchPlayer, currentRackLetters has', gameWithNextPlayer.currentRackLetters.length, 'balls');
      console.log('After switchPlayer, player scores:', gameWithNextPlayer.players.map(p => `${p.name}: ${p.score}`).join(', '));

      const backToPoolGame = updatePhase(gameWithNextPlayer, 'pool');
      console.log('After updatePhase, player scores:', backToPoolGame.players.map(p => `${p.name}: ${p.score}`).join(', '));

      store.setGame(backToPoolGame);
      console.log('Game state set in store before starting PoolScene');
      this.scene.start('PoolScene');
    });
  }
  
  private onClearLetters(): void {
    // Return all placed letters to hand
    const letterIds = Array.from(this.placedLetters.keys());
    letterIds.forEach(id => this.returnTileToHand(id, false));
    this.showMessage('Letters returned to hand', 'success');
  }
  
  private onPass(): void {
    // Check if any letters are placed
    if (this.placedLetters.size > 0) {
      this.showMessage('Clear letters before passing', 'error');
      return;
    }
    
    this.timerExpired = true; // Prevent timer from firing after turn ends
    
    const store = useGameStore.getState();
    const currentGame = store.game!;
    const currentPlayer = getCurrentPlayer(currentGame);
    
    // Increment consecutive passes for this player
    const gameWithPass = incrementConsecutivePasses(currentGame, currentPlayer.id);
    
    // Check for stalemate condition
    if (isStalemate(gameWithPass)) {
      const threshold = gameWithPass.rules.stalemateThreshold;
      console.log(`🛑 STALEMATE DETECTED: All players have passed ${threshold} consecutive turns`);
      const completedGame = updateStatus(gameWithPass, 'completed');
      store.setGame(completedGame);
      this.endMultiplayerTurnIfNeeded(completedGame, { gameCompleted: true });

      // Show stalemate message
      this.showMessage(`STALEMATE! All players passed ${threshold} turns.\nGame over!`, 'warning');

      // Navigate to game over scene after delay
      this.time.delayedCall(3500, () => {
        this.scene.start('GameOverScene');
      });
      return;
    }

    this.endMultiplayerTurnIfNeeded(gameWithPass);

    if (this.isMultiplayerSessionActive()) {
      // Multiplayer authority: turn owner advances only on server turn_change.
      const backToPoolGame = updatePhase(gameWithPass, 'pool');
      store.setGame(backToPoolGame);
      this.scene.start('PoolScene');
      return;
    }
    
    // Switch to next player and go back to pool
    const gameWithNextPlayer = switchPlayer(gameWithPass);
    console.log('🔄 BoardScene (pass): After switchPlayer, currentRackLetters has', gameWithNextPlayer.currentRackLetters.length, 'balls');
    const backToPoolGame = updatePhase(gameWithNextPlayer, 'pool');
    store.setGame(backToPoolGame);
    this.scene.start('PoolScene');
  }
  
  private onEndGame(): void {
    const store = useGameStore.getState();
    const game = store.game!;

    const completedGame = {
      ...game,
      status: 'completed' as const,
    };
    store.setGame(completedGame);
    this.endMultiplayerTurnIfNeeded(completedGame, { gameCompleted: true });
    this.scene.start('GameOverScene');
  }
  
  private handleRestoreFailed(message: string): void {
    console.warn('[BoardScene] Session restore failed:', message);
    this.showMessage('Session could not be restored.\nReturning to menu...', 'warning');
    this.time.delayedCall(2500, () => {
      this.scene.start('MenuScene');
    });
  }

  private showMessage(text: string, type: 'success' | 'error' | 'warning' | 'info' = 'info'): void {
    // Determine colors based on message type
    const colorScheme = type === 'success' ? COLORS.success :
                       type === 'error' ? COLORS.error :
                       type === 'warning' ? COLORS.warning : COLORS.info;
    
    // Show temporary message
    const message = this.add.text(640, 350, text, {
      fontSize: TYPOGRAPHY.fontSize.lg,
      color: colorScheme.text,
      backgroundColor: colorScheme.bg,
      padding: { x: 16, y: 8 },
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(200);
    
    this.time.delayedCall(2000, () => {
      message.destroy();
    });
  }
  
  private renderUI(playerName: string): void {
    const store = useGameStore.getState();
    const game = store.game!;
    const currentPlayer = getCurrentPlayer(game);
    const playerColor = getPlayerColor(currentPlayer.id);
    
    // Player info with color indicator (fixed to camera)
    const playerInfoY = 2;
    
    // Color box
    this.add.rectangle(20, playerInfoY + 15, 20, 20, playerColor.primary)
      .setOrigin(0, 0.5)
      .setScrollFactor(0)
      .setDepth(100);
    
    // Player name
    this.add.text(45, playerInfoY, `${playerName}'s Turn`, {
      fontSize: '24px',
      color: '#ffff00',
      backgroundColor: '#000000',
      padding: { x: 10, y: 5 },
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }).setScrollFactor(0).setDepth(100);
    
    // Timer display (if timer is enabled)
    if (this.timerDurationMs > 0) {
      this.timerText = this.add.text(230, playerInfoY, '', {
        fontSize: '24px',
        color: '#00ff00',
        backgroundColor: '#000000',
        padding: { x: 10, y: 5 },
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace',
      }).setScrollFactor(0).setDepth(100);
    }
    
    // Score display with player colors (moved to top-right, away from hand)
    let scoreY = 5;
    game.players.forEach(player => {
      const pColor = getPlayerColor(player.id);
      const isCurrentPlayer = player.id === currentPlayer.id;
      const consecutivePasses = game.consecutivePassesPerPlayer[player.id] || 0;
      
      // Player score card with modern styling
      const cardWidth = 200;
      const cardHeight = 36;
      const cardX = 1050; // Right side alignment with hand
      
      const scoreCard = this.add.graphics();
      scoreCard.fillStyle(isCurrentPlayer ? COLORS.primary.main : COLORS.background.tertiary, isCurrentPlayer ? 0.3 : 0.6);
      scoreCard.fillRoundedRect(cardX, scoreY - 4, cardWidth, cardHeight, 6);
      if (isCurrentPlayer) {
        scoreCard.lineStyle(2, COLORS.primary.main, 1);
        scoreCard.strokeRoundedRect(cardX, scoreY - 4, cardWidth, cardHeight, 6);
      }
      scoreCard.setScrollFactor(0).setDepth(99);
      
      // Color indicator circle
      const colorCircle = this.add.graphics();
      colorCircle.fillStyle(pColor.primary, 1);
      colorCircle.fillCircle(cardX + 14, scoreY + 14, 7);
      colorCircle.setScrollFactor(0).setDepth(100);
      
      // Player text with pass warning
      const playerText = `${player.name}  ${player.score}`;
      const passWarning = consecutivePasses >= 3 ? ` ⚠️${consecutivePasses}` : consecutivePasses > 0 ? ` (${consecutivePasses})` : '';
      const fullText = playerText + passWarning;
      
      const scoreText = this.add.text(cardX + 30, scoreY + 14, fullText, {
        fontSize: TYPOGRAPHY.fontSize.sm,
        color: isCurrentPlayer ? COLORS.text.accent : consecutivePasses >= 3 ? COLORS.warning.text : COLORS.text.primary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      }).setOrigin(0, 0.5).setScrollFactor(0).setDepth(100);
      
      // Store reference for updates
      this.scoreTexts.set(player.id, scoreText);
      
      scoreY += 44;
    });
    
    // Instructions with modern styling (positioned above column headers)
    const instructionsBg = this.add.graphics();
    instructionsBg.fillStyle(COLORS.background.secondary, 0.9);
    instructionsBg.fillRoundedRect(380, 2, 500, 32, 8);
    instructionsBg.setScrollFactor(0).setDepth(99);
    
    this.add.text(630, 18, 'Drag letters to board • Right-click: return • Wheel: zoom • Middle-click drag: pan', {
      fontSize: TYPOGRAPHY.fontSize.xs,
      color: COLORS.text.primary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100);
  }
  
  /**
   * Update the score display for all players
   */
  private updateScoreDisplay(): void {
    const store = useGameStore.getState();
    const game = store.game!;
    const currentPlayer = getCurrentPlayer(game);
    
    game.players.forEach(player => {
      const scoreText = this.scoreTexts.get(player.id);
      if (scoreText) {
        const isCurrentPlayer = player.id === currentPlayer.id;
        const consecutivePasses = game.consecutivePassesPerPlayer[player.id] || 0;
        const playerText = `${player.name}  ${player.score}`;
        const passWarning = consecutivePasses >= 3 ? ` ⚠️${consecutivePasses}` : consecutivePasses > 0 ? ` (${consecutivePasses})` : '';
        const fullText = playerText + passWarning;
        
        scoreText.setText(fullText);
        scoreText.setStyle({
          color: isCurrentPlayer ? COLORS.text.accent : consecutivePasses >= 3 ? COLORS.warning.text : COLORS.text.primary,
        });
      }
    });
  }
  
  private renderBoard(): void {
    this.boardGraphics = this.add.graphics();
    this.boardContainer.add(this.boardGraphics);
    
    // Draw background
    this.boardGraphics.fillStyle(0x2a2a2a, 1);
    this.boardGraphics.fillRect(
      this.gridOffsetX - 5,
      this.gridOffsetY - 5,
      this.cellSize * BOARD_SIZE + 10,
      this.cellSize * BOARD_SIZE + 10
    );
    
    // Draw each cell
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        const cell = getCell(this.board, { row, col });
        if (!cell) continue;
        
        const x = this.gridOffsetX + col * this.cellSize;
        const y = this.gridOffsetY + row * this.cellSize;
        
        // Determine cell color
        let fillColor = 0x1a1a1a; // Default dark gray
        let strokeColor = 0x444444; // Default grid line
        let strokeWidth = 1;
        
        // Check if center square
        if (row === CENTER_POSITION.row && col === CENTER_POSITION.col) {
          fillColor = 0xFFD700; // Gold for center
          strokeColor = 0xFFA500; // Orange border
          strokeWidth = 3;
        }
        // Check for bonus square
        else if (cell.bonusType) {
          fillColor = this.BONUS_COLORS[cell.bonusType];
          strokeWidth = 1;
        }
        
        // Draw cell
        this.boardGraphics.fillStyle(fillColor, 1);
        this.boardGraphics.fillRect(x, y, this.cellSize, this.cellSize);
        
        // Draw border
        this.boardGraphics.lineStyle(strokeWidth, strokeColor, 1);
        this.boardGraphics.strokeRect(x, y, this.cellSize, this.cellSize);
        
        // Add bonus type label (skip only the exact center square, not the whole row/column)
        if (cell.bonusType && !(row === CENTER_POSITION.row && col === CENTER_POSITION.col)) {
          const label = this.add.text(x + this.cellSize / 2, y + this.cellSize / 2, cell.bonusType, {
            fontSize: '10px',
            color: '#ffffff',
            fontStyle: 'bold',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }).setOrigin(0.5);
          this.boardContainer.add(label);
        }
        
        // Add star to center square
        if (row === CENTER_POSITION.row && col === CENTER_POSITION.col) {
          const star = this.add.text(x + this.cellSize / 2, y + this.cellSize / 2, '★', {
            fontSize: '20px',
            color: '#ffffff',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }).setOrigin(0.5);
          this.boardContainer.add(star);
        }
      }
    }
    
    // Add row/column labels
    this.addGridLabels();
  }
  
  private addGridLabels(): void {
    // Column labels (A-U)
    for (let col = 0; col < BOARD_SIZE; col++) {
      const x = this.gridOffsetX + col * this.cellSize + this.cellSize / 2;
      const y = this.gridOffsetY - 20;
      const label = String.fromCharCode(65 + col); // A=65
      const text = this.add.text(x, y, label, {
        fontSize: '12px',
        color: '#888888',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }).setOrigin(0.5);
      this.boardContainer.add(text);
    }
    
    // Row labels (1-21)
    for (let row = 0; row < BOARD_SIZE; row++) {
      const x = this.gridOffsetX - 20;
      const y = this.gridOffsetY + row * this.cellSize + this.cellSize / 2;
      const label = (row + 1).toString();
      const text = this.add.text(x, y, label, {
        fontSize: '12px',
        color: '#888888',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }).setOrigin(0.5);
      this.boardContainer.add(text);
    }
  }
  
  /**
   * Render all letters that have been placed on the board in previous turns
   */
  private renderPlacedWords(): void {
    // Clear any existing permanent letter containers to avoid duplicates
    this.permanentLetterContainers.forEach(container => container.destroy());
    this.permanentLetterContainers = [];
    
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        const cell = getCell(this.board, { row, col });
        
        // Skip empty cells
        if (!cell || !cell.isOccupied || !cell.letter) {
          continue;
        }
        
        // Calculate screen position
        const x = this.gridOffsetX + col * this.cellSize + this.cellSize / 2;
        const y = this.gridOffsetY + row * this.cellSize + this.cellSize / 2;
        
        // Get player color for border
        const playerColor = getPlayerColor(cell.playerId!);
        
        // Create letter tile background (slightly smaller than cell to show grid)
        const tileSize = this.cellSize - 4;
        const background = this.add.rectangle(0, 0, tileSize, tileSize, 0xF5F5DC)
          .setStrokeStyle(3, playerColor.primary); // Colored border showing who played it
        
        // Create letter text
        const letterText = this.add.text(0, 0, cell.letter.character, {
          fontSize: '20px',
          color: '#000000',
          fontStyle: 'bold',
          fontFamily: TYPOGRAPHY.fontFamily.default,
        }).setOrigin(0.5);

        // Create points text (bottom right corner)
        const pointsText = this.add.text(tileSize / 2 - 6, tileSize / 2 - 6, cell.letter.points.toString(), {
          fontSize: '12px',
          color: '#666666',
          fontFamily: TYPOGRAPHY.fontFamily.default,
        }).setOrigin(1, 1);

        // Create container for the letter tile
        const tileContainer = this.add.container(x, y, [background, letterText, pointsText]);
        
        // Track this container so we can clear it later
        this.permanentLetterContainers.push(tileContainer);
        
        // Add to board container so it moves with zoom/pan
        this.boardContainer.add(tileContainer);
      }
    }
  }
  
  private setupCameraControls(): void {
    // Mouse wheel zoom
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.input.on('wheel', (_pointer: any, _gameObjects: any, _deltaX: number, deltaY: number) => {
      const camera = this.cameras.main;
      const zoomAmount = deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Phaser.Math.Clamp(camera.zoom + zoomAmount, 0.5, 2);
      camera.setZoom(newZoom);
    });
    
    // Middle-click (or Ctrl+drag) to pan the board
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Start panning if middle-clicking or ctrl+left-click on empty space
      if (pointer.middleButtonDown() || (pointer.leftButtonDown() && pointer.event.ctrlKey)) {
        const objectsUnderPointer = this.input.hitTestPointer(pointer);
        // Only pan if not clicking on interactive objects
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (objectsUnderPointer.length === 0 || objectsUnderPointer.every((obj: any) => !obj.input)) {
          isDragging = true;
          dragStartX = pointer.x;
          dragStartY = pointer.y;
          this.input.setDefaultCursor('grab');
        }
      }
    });
    
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (isDragging) {
        const camera = this.cameras.main;
        const deltaX = pointer.x - dragStartX;
        const deltaY = pointer.y - dragStartY;
        camera.scrollX -= deltaX / camera.zoom;
        camera.scrollY -= deltaY / camera.zoom;
        dragStartX = pointer.x;
        dragStartY = pointer.y;
        this.input.setDefaultCursor('grabbing');
      }
    });
    
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (pointer.middleButtonReleased() || pointer.leftButtonReleased()) {
        if (isDragging) {
          isDragging = false;
          this.input.setDefaultCursor('default');
        }
      }
    });
  }
  
  private renderHandTiles(hand: Letter[]): void {
    // Clear existing tiles
    this.handTiles.forEach(tile => tile.destroy());
    this.handTiles.clear();
    
    // Clear existing label
    if (this.handLabel) {
      this.handLabel.destroy();
    }
    
    // Calculate hand position to be below player scores
    const store = useGameStore.getState();
    const game = store.game!;
    const numPlayers = game.players.length;
    const scoreAreaHeight = 20 + (numPlayers * 44); // Score cards start at Y=20, each is 44px tall
    const handLabelY = scoreAreaHeight + 20; // 20px buffer below scores
    const handTilesStartY = handLabelY + 35; // Start tiles 35px below label
    
    // Determine layout: use two columns if more than 5 tiles
    const useDoubleColumn = hand.length > 5;
    const spacing = this.tileSize + 10;
    const columnSpacing = this.tileSize + 15; // Horizontal spacing between columns
    
    // Add "Your Hand" label (positioned below scores, above the hand tiles)
    const labelX = useDoubleColumn ? this.handX + this.tileSize + 7 : this.handX + this.tileSize / 2;
    this.handLabel = this.add.text(labelX, handLabelY, 'Your Hand', {
      fontSize: TYPOGRAPHY.fontSize.base,
      color: COLORS.text.primary,
      backgroundColor: '#334155',
      padding: { x: 10, y: 5 },
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100);
    
    hand.forEach((letter, index) => {
      let x: number;
      let y: number;
      
      if (useDoubleColumn) {
        // Two columns: first column for first half, second column for rest
        const column = index < Math.ceil(hand.length / 2) ? 0 : 1;
        const rowInColumn = column === 0 ? index : index - Math.ceil(hand.length / 2);
        x = this.handX + this.tileSize / 2 + (column * columnSpacing);
        y = handTilesStartY + rowInColumn * spacing;
      } else {
        // Single column
        x = this.handX + this.tileSize / 2;
        y = handTilesStartY + index * spacing;
      }
      
      const tile = this.createLetterTile(letter, x, y);
      this.handTiles.set(letter.id, tile);
      this.handContainer.add(tile);
    });
  }
  
  private createLetterTile(letter: Letter, x: number, y: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    
    // Tile background
    const bg = this.add.rectangle(0, 0, this.tileSize, this.tileSize, 0xF5DEB3); // Tan/beige
    bg.setStrokeStyle(2, 0x8B4513); // Brown border
    container.add(bg);
    
    // Letter text
    const letterText = this.add.text(0, -5, letter.character.toUpperCase(), {
      fontSize: '28px',
      color: '#000000',
      fontStyle: 'bold',
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5);
    container.add(letterText);
    
    // Point value (small subscript)
    const pointText = this.add.text(15, 15, letter.points.toString(), {
      fontSize: '16px',
      color: '#000000',
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5);
    container.add(pointText);
    
    // Make interactive
    container.setSize(this.tileSize, this.tileSize);
    container.setInteractive({ draggable: true, useHandCursor: true });
    
    // Store original position for snapping back
    container.setData('originalX', x);
    container.setData('originalY', y);
    container.setData('letterId', letter.id);
    container.setData('inHand', true);
    
    // Drag events
    container.on('dragstart', () => {
      container.setDepth(200);
      bg.setStrokeStyle(3, 0xFFD700); // Gold highlight when dragging
    });
    
    container.on('drag', (_pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      container.setPosition(dragX, dragY);
    });
    
    container.on('dragend', (_pointer: Phaser.Input.Pointer) => {
      this.handleTileDrop(container, letter);
    });
    
    // Right-click to return to hand (if placed on board)
    container.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown() && !container.getData('inHand')) {
        this.returnTileToHand(letter.id);
      }
    });
    
    return container;
  }
  
  private handleTileDrop(container: Phaser.GameObjects.Container, letter: Letter): void {
    const letterId = letter.id;
    
    // Get drop position
    const worldX = container.x;
    const worldY = container.y;
    
    // Convert to grid coordinates
    const gridPos = this.screenToGrid(worldX, worldY);
    
    // Check if dropped on valid board cell
    if (gridPos && this.isValidDropPosition(gridPos)) {
      // Place on board
      this.placeLetterOnBoard(letterId, gridPos, container);
    } else {
      // Snap back to hand
      this.returnTileToHand(letterId, true);
    }
  }
  
  private screenToGrid(screenX: number, screenY: number): { row: number; col: number } | null {
    // Calculate grid position
    const col = Math.floor((screenX - this.gridOffsetX) / this.cellSize);
    const row = Math.floor((screenY - this.gridOffsetY) / this.cellSize);
    
    // Check bounds
    if (row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE) {
      return { row, col };
    }
    
    return null;
  }
  
  private isValidDropPosition(position: { row: number; col: number }): boolean {
    const multiplayerManager = MultiplayerManager.getInstance();
    if (multiplayerManager.isBoardInteractionBlocked?.()) {
      return false;
    }

    const store = useGameStore.getState();
    if (store.game) {
      const rehydratedGame = this.applyAuthoritativeSnapshot(store.game);
      if (rehydratedGame !== store.game) {
        store.setGame(rehydratedGame);
      }
      this.board = rehydratedGame.board;
    }

    // Check if cell is empty (no letter already placed there)
    for (const [, data] of this.placedLetters) {
      if (data.position.row === position.row && data.position.col === position.col) {
        return false;
      }
    }
    
    // Check if cell already has a letter from previous turns
    const cell = getCell(this.board, position);
    if (cell && cell.letter) {
      return false;
    }
    
    return true;
  }
  
  private placeLetterOnBoard(
    letterId: string,
    position: { row: number; col: number },
    container: Phaser.GameObjects.Container
  ): void {
    // Calculate center of cell
    const centerX = this.gridOffsetX + position.col * this.cellSize + this.cellSize / 2;
    const centerY = this.gridOffsetY + position.row * this.cellSize + this.cellSize / 2;
    
    // Snap to grid
    container.setPosition(centerX, centerY);
    container.setData('inHand', false);
    container.setDepth(150);
    
    // Remove from hand container, add to board container
    this.handContainer.remove(container);
    this.boardContainer.add(container);
    
    // Track placement
    this.placedLetters.set(letterId, {
      letterId,
      position,
      sprite: container,
    });
    
    // Scale down to fit cell better
    container.setScale(0.6);
    
    console.log(`Placed letter ${letterId} at (${position.row}, ${position.col})`);
    
    // Update word preview
    this.updateWordPreview();
  }
  
  private returnTileToHand(letterId: string, animate = false): void {
    const tile = this.handTiles.get(letterId);
    if (!tile) return;
    
    const originalX = tile.getData('originalX');
    const originalY = tile.getData('originalY');
    
    // Remove from placed letters if it was placed
    const placed = this.placedLetters.get(letterId);
    if (placed) {
      this.placedLetters.delete(letterId);
      this.boardContainer.remove(tile);
      this.handContainer.add(tile);
    }
    
    // Reset state
    tile.setData('inHand', true);
    tile.setDepth(50);
    tile.setScale(1);
    
    if (animate) {
      // Animate back to hand position
      this.tweens.add({
        targets: tile,
        x: originalX,
        y: originalY,
        duration: 200,
        ease: 'Back.easeOut',
      });
    } else {
      tile.setPosition(originalX, originalY);
    }
    
    console.log(`Returned letter ${letterId} to hand`);
    
    // Update word preview
    this.updateWordPreview();
  }
  
  /**
   * T072: Update word preview showing forming words and scores
   */
  private updateWordPreview(): void {
    // Clear existing preview
    if (this.wordPreviewText) {
      this.wordPreviewText.destroy();
      this.wordPreviewText = undefined;
    }
    
    // If no letters placed, nothing to preview
    if (this.placedLetters.size === 0) {
      return;
    }
    
    const store = useGameStore.getState();
    const game = store.game;
    if (!game) return;
    
    const currentPlayer = getCurrentPlayer(game);
    
    // Convert placed letters to the format needed for validation
    const placedLetterData: PlacedLetterPosition[] = [];
    this.placedLetters.forEach((data, letterId) => {
      const letter = currentPlayer.hand.find(l => l.id === letterId);
      if (letter) {
        placedLetterData.push({
          letterId: letter.id,
          position: data.position,
          letter: letter.character,
        });
      }
    });
    
    if (placedLetterData.length === 0) return;
    
    // Get the formed word (this includes existing board letters)
    try {
      const formedWord = getFormedWord(placedLetterData, game.board);
      
      if (formedWord.length < 2) {
        this.wordPreviewText = this.add.text(640, 650, 'Word must be at least 2 letters', {
          fontSize: TYPOGRAPHY.fontSize.base,
          color: COLORS.warning.text,
          backgroundColor: COLORS.warning.bg,
          padding: { x: 12, y: 6 },
        }).setOrigin(0.5).setScrollFactor(0).setDepth(100);
        return;
      }
      
      // Get complete word info for scoring
      const { positions: allPositions, newlyPlacedPositions } = 
        getCompleteWordForScoring(placedLetterData, game.board);
      
      // Build Letter array for scoring
      const allLetters: Letter[] = [];
      for (const position of allPositions) {
        const posKey = `${position.row},${position.col}`;
        
        if (newlyPlacedPositions.has(posKey)) {
          const placedLetter = placedLetterData.find(
            pl => pl.position.row === position.row && pl.position.col === position.col
          );
          if (placedLetter) {
            const letter = currentPlayer.hand.find(l => l.id === placedLetter.letterId);
            if (letter) allLetters.push(letter);
          }
        } else {
          const cell = getCell(game.board, position);
          if (cell && cell.letter) allLetters.push(cell.letter);
        }
      }
      
      // Calculate main word score
      const mainWordScore = calculateWordScore(
        allLetters,
        allPositions,
        game.board,
        newlyPlacedPositions
      );
      
      // Get crosswords
      const crosswords = getCrosswords(placedLetterData, game.board);
      let totalScore = mainWordScore.totalScore;
      
      // Calculate crossword scores
      const crosswordScores: { word: string; score: number }[] = [];
      for (const cw of crosswords) {
        const cwPositions = cw.positions;
        const cwNewPositions = cw.newlyPlacedPositions;
        
        const cwLetters: Letter[] = [];
        for (const pos of cwPositions) {
          const posKey = `${pos.row},${pos.col}`;
          if (cwNewPositions.has(posKey)) {
            const placedLetter = placedLetterData.find(
              pl => pl.position.row === pos.row && pl.position.col === pos.col
            );
            if (placedLetter) {
              const letter = currentPlayer.hand.find(l => l.id === placedLetter.letterId);
              if (letter) cwLetters.push(letter);
            }
          } else {
            const cell = getCell(game.board, pos);
            if (cell && cell.letter) cwLetters.push(cell.letter);
          }
        }
        
        const cwScore = calculateWordScore(
          cwLetters,
          cwPositions,
          game.board,
          cwNewPositions
        );
        
        crosswordScores.push({ word: cw.word, score: cwScore.totalScore });
        totalScore += cwScore.totalScore;
      }
      
      // Build preview message
      let previewText = `${formedWord.toUpperCase()}: ${mainWordScore.totalScore} pts`;
      
      if (crosswordScores.length > 0) {
        previewText += '\n' + crosswordScores.map(cw => 
          `${cw.word.toUpperCase()}: ${cw.score} pts`
        ).join('\n');
        previewText += `\nTotal: ${totalScore} pts`;
      }
      
      this.wordPreviewText = this.add.text(640, 650, previewText, {
        fontSize: TYPOGRAPHY.fontSize.base,
        color: COLORS.info.text,
        backgroundColor: COLORS.info.bg,
        padding: { x: 12, y: 6 },
        align: 'center',
      }).setOrigin(0.5).setScrollFactor(0).setDepth(100);
      
    } catch (error) {
      console.error('Error generating word preview:', error);
    }
  }
}

export default BoardScene;
